import { Component, ReactNode } from 'react';

interface State { hasError: boolean; error: Error | null }

/**
 * Top-level error boundary. Catches render-time crashes so React doesn't
 * unmount the whole app tree (which previously bubbled up and the user
 * perceived as being "kicked back to login"). Offers a Reload button
 * that does a hard reload — the next mount goes through normal session
 * restore and lands the user on the correct screen.
 */
export class VendorErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // Local-only log. No remote reporting per product decision.
    console.error('[VendorErrorBoundary] render crash', error, info);
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <h1 className="text-lg font-semibold text-foreground">Something went wrong</h1>
        <p className="max-w-xs text-sm text-muted-foreground">
          The app ran into an unexpected error. Please reload to continue —
          your session is safe.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="rounded-xl bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-sm active:scale-[0.98]"
        >
          Reload App
        </button>
      </div>
    );
  }
}